import React, { useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
type Motif = 'atom' | 'molecule' | 'electron';
interface FlowItem {
  id: number;
  motif: Motif;
  size: number;
  /** Odległość od prawej krawędzi (vw). */
  right: number;
  /** Maksymalny zakręt w lewo (px). */
  curve: number;
  duration: number;
  delay: number;
  opacity: number;
}
const MOTIFS: Motif[] = ['atom', 'molecule', 'electron'];
const NAVY = '#0B1D3A';
const ELECTRIC = '#2350C8';
const TURQUOISE = '#4FB6C9';
const NUCLEUS_RED = '#C0392B';
function buildItems(count: number, mobile: boolean): FlowItem[] {
  const rand = (min: number, max: number) => min + Math.random() * (max - min);
  const offset = Math.floor(Math.random() * MOTIFS.length);
  return Array.from({
    length: count
  }, (_, i) => ({
    id: i,
    motif: MOTIFS[(i + offset) % MOTIFS.length],
    size: mobile ? rand(42, 56) : rand(95, 130),
    right: mobile ? rand(1, 4) : rand(2, 7),
    curve: mobile ? 26 : rand(80, 110),
    duration: rand(11, 14),
    delay: -(i * 6 + rand(0, 3)),
    opacity: mobile ? rand(0.35, 0.42) : rand(0.38, 0.48)
  }));
}
interface MotifProps {
  size: number;
  uid: string;
  animate: boolean;
}

/** Tor orbity jako ścieżka — elektron krąży po niej dzięki animateMotion. */
function orbitPath(rx: number, ry: number): string {
  return `M${48 - rx},48 a${rx},${ry} 0 1,0 ${rx * 2},0 a${rx},${ry} 0 1,0 ${-rx * 2},0`;
}
function AtomMotif({
  size,
  uid,
  animate
}: MotifProps) {
  return <svg width={size} height={size} viewBox="0 0 96 96" fill="none" aria-hidden="true" data-id="element-52" data-mp="3rzvhd1sf3iea">
      <defs data-id="element-53" data-mp="lhch2nkxfna">
        <radialGradient id={`${uid}-blue`} cx="34%" cy="28%" data-id="element-54" data-mp="lppqvnm6mz0">
          <stop offset="0%" stopColor="#7FA8F5" data-id="element-55" data-mp="5l2n4ac43cfu" />
          <stop offset="100%" stopColor={NAVY} data-id="element-56" data-mp="5jr8ffh4ok9y" />
        </radialGradient>
        <radialGradient id={`${uid}-red`} cx="34%" cy="28%" data-id="element-57" data-mp="akoyee8dqim0">
          <stop offset="0%" stopColor="#EC8172" data-id="element-58" data-mp="ai2kzxyt58vv" />
          <stop offset="100%" stopColor={NUCLEUS_RED} data-id="element-59" data-mp="fkbdyubokaxz" />
        </radialGradient>
        <linearGradient id={`${uid}-orbit`} x1="0" y1="0" x2="1" y2="1" data-id="element-60" data-mp="3lzz4575jdodq">
          <stop offset="0%" stopColor={ELECTRIC} data-id="element-61" data-mp="3ly5tlgrsyk0g" />
          <stop offset="100%" stopColor={TURQUOISE} data-id="element-62" data-mp="3h2gvzizcfd6s" />
        </linearGradient>
        <radialGradient id={`${uid}-electron`} cx="35%" cy="30%" data-id="element-63" data-mp="3vqttike2x43e">
          <stop offset="0%" stopColor="#A9C7FF" data-id="element-64" data-mp="3vs4wwj3m49tz" />
          <stop offset="100%" stopColor={ELECTRIC} data-id="element-65" data-mp="3qyk3l4cys414" />
        </radialGradient>
      </defs>

      {/* Trzy orbity, po każdej krąży jeden elektron */}
      {[0, 60, 120].map((angle, i) => <g key={angle} transform={`rotate(${angle} 48 48)`} data-id="element-66" data-mp="3n0zwo1po9dr" data-mpi={i}>
          <path id={`${uid}-orbit-${i}`} d={orbitPath(42, 16)} stroke={`url(#${uid}-orbit)`} strokeWidth="1.8" fill="none" data-id="element-67" data-mp="3ovl83gwa3id" />
          <g transform={animate ? undefined : 'translate(90 48)'} data-id="element-68" data-mp="dkyo5bjkm66d">
            <circle r="4.2" fill={`url(#${uid}-electron)`} data-id="element-69" data-mp="dim5ew5znrs9" />
            <circle cx="-1.4" cy="-1.4" r="1.4" fill="#FFFFFF" opacity="0.75" data-id="element-70" data-mp="2kc6evpqgidgm" />
            {animate && <animateMotion dur={`${9 + i * 2}s`} repeatCount="indefinite" keyPoints={i % 2 === 0 ? '0;1' : '1;0'} keyTimes="0;1" calcMode="linear" data-id="element-71" data-mp="2fd3lx3nr3xp0">
                <mpath href={`#${uid}-orbit-${i}`} data-id="element-72" data-mp="uwdms6qy286x" />
              </animateMotion>}
          </g>
        </g>)}

      {/* Jądro: małe czerwone i niebieskie sfery */}
      <g data-id="element-73" data-mp="l3ew8jbw07ko">
        <circle cx="44" cy="45" r="6.4" fill={`url(#${uid}-red)`} data-id="element-74" data-mp="l3onsbckymsh" />
        <circle cx="52" cy="47" r="6.4" fill={`url(#${uid}-blue)`} data-id="element-75" data-mp="q2j3v5j5xx8w" />
        <circle cx="47" cy="53" r="6" fill={`url(#${uid}-blue)`} data-id="element-76" data-mp="c3jic6qy5ygy" />
        <circle cx="52.5" cy="41" r="5.4" fill={`url(#${uid}-red)`} data-id="element-77" data-mp="c28cm1dxygn7" />
        <circle cx="42" cy="51" r="5" fill={`url(#${uid}-red)`} data-id="element-78" data-mp="g66c4d76htgs" />
        <circle cx="42.2" cy="43" r="1.9" fill="#FFFFFF" opacity="0.55" data-id="element-79" data-mp="25ltpi56dznt" />
      </g>
    </svg>;
}
function MoleculeMotif({
  size,
  uid,
  animate
}: MotifProps) {
  return <svg width={size} height={size} viewBox="0 0 96 96" fill="none" aria-hidden="true" data-id="element-80" data-mp="74ox6y5ibxto">
      <defs data-id="element-81" data-mp="1wjwm20kzbbyc">
        <radialGradient id={`${uid}-navy`} cx="34%" cy="28%" data-id="element-82" data-mp="1whabrt6peicf">
          <stop offset="0%" stopColor="#6FA0FF" data-id="element-83" data-mp="21heuzqyzxr6c" />
          <stop offset="100%" stopColor={NAVY} data-id="element-84" data-mp="21g3o3ia983b0" />
        </radialGradient>
        <radialGradient id={`${uid}-turq`} cx="34%" cy="28%" data-id="element-85" data-mp="1ffalpt71tsk">
          <stop offset="0%" stopColor="#B4EDF4" data-id="element-86" data-mp="3sm9porkqgysn" />
          <stop offset="100%" stopColor={TURQUOISE} data-id="element-87" data-mp="3skf0h12fikir" />
        </radialGradient>
        <radialGradient id={`${uid}-elec`} cx="34%" cy="28%" data-id="element-88" data-mp="3ojd1mlcfcahb">
          <stop offset="0%" stopColor="#8FB6FF" data-id="element-89" data-mp="3jmmlxz4j7gwf" />
          <stop offset="100%" stopColor={ELECTRIC} data-id="element-90" data-mp="3jnxppyxz8onj" />
        </radialGradient>
      </defs>

      <g className={animate ? 'chem-rotate-slow' : undefined} data-id="element-91" data-mp="3eo1jru5pyn5c">
        <g stroke={NAVY} strokeOpacity="0.55" strokeWidth="3" strokeLinecap="round" data-id="element-92" data-mp="39vrw6ks2tz68">
          <path d="M48 46 22 62" data-id="element-93" data-mp="34wfesfjz5kb1" />
          <path d="M48 46 76 56" data-id="element-94" data-mp="34xgp2ht622rp" />
          <path d="M48 46 50 18" data-id="element-95" data-mp="34ukoelpxozzk" />
          <path d="M22 62 26 84" data-id="element-96" data-mp="30w4wq4iwr3ft" />
          <path d="M76 56 84 76" data-id="element-97" data-mp="30wwpl7l4en2e" />
        </g>

        <circle cx="48" cy="46" r="13" fill={`url(#${uid}-navy)`} data-id="element-98" data-mp="2vvzbb638ed66" />
        <circle cx="22" cy="62" r="9.5" fill={`url(#${uid}-turq)`} data-id="element-99" data-mp="2vybpd85vc3dy" />
        <circle cx="76" cy="56" r="9" fill={`url(#${uid}-elec)`} data-id="element-100" data-mp="2qygyh4m3fdsw" />
        <circle cx="50" cy="18" r="8" fill={`url(#${uid}-turq)`} data-id="element-101" data-mp="16fmyjymop5er" />
        <circle cx="26" cy="84" r="6.5" fill={`url(#${uid}-elec)`} data-id="element-102" data-mp="16hhl1hdlt5ky" />
        <circle cx="84" cy="76" r="6" fill={`url(#${uid}-turq)`} data-id="element-103" data-mp="1bcn50ang4a6e" />

        <circle cx="43.5" cy="41" r="3.4" fill="#FFFFFF" opacity="0.45" data-id="element-104" data-mp="1gd1fdzcirw7l" />
        <circle cx="19" cy="58.5" r="2.2" fill="#FFFFFF" opacity="0.45" data-id="element-105" data-mp="1kewd6tv8rrgm" />
      </g>
    </svg>;
}
function ElectronOrbitMotif({
  size,
  uid,
  animate
}: MotifProps) {
  return <svg width={size} height={size} viewBox="0 0 96 96" fill="none" aria-hidden="true" data-id="element-106" data-mp="nqglos8kn80v">
      <defs data-id="element-107" data-mp="so8io0o2pkbx">
        <linearGradient id={`${uid}-track`} x1="0" y1="1" x2="1" y2="0" data-id="element-108" data-mp="slm95vhmgur2">
          <stop offset="0%" stopColor={TURQUOISE} stopOpacity="0.25" data-id="element-109" data-mp="woinha04q4p7" />
          <stop offset="100%" stopColor={ELECTRIC} data-id="element-110" data-mp="11gad9ibgo6ea" />
        </linearGradient>
        <radialGradient id={`${uid}-dot`} cx="35%" cy="30%" data-id="element-111" data-mp="4y4ffuesrmlg">
          <stop offset="0%" stopColor="#B7D0FF" data-id="element-112" data-mp="4xvy9qbn5v4g" />
          <stop offset="100%" stopColor={ELECTRIC} data-id="element-113" data-mp="8z7r04ptawgv" />
        </radialGradient>
      </defs>

      <g transform="rotate(-28 48 48)" data-id="element-114" data-mp="1cadhi2f80fqz">
        <path id={`${uid}-track-path`} d={orbitPath(42, 22)} stroke={`url(#${uid}-track)`} strokeWidth="2" fill="none" data-id="element-115" data-mp="1c9c4vtik3l75" />
        <path d={orbitPath(30, 15)} stroke={TURQUOISE} strokeOpacity="0.4" strokeWidth="1.4" strokeDasharray="4 7" fill="none" data-id="element-116" data-mp="17bad8k72edk2" />

        <g transform={animate ? undefined : 'translate(90 48)'} data-id="element-117" data-mp="1h1dh5ffq5bx6">
          <circle r="9" fill={ELECTRIC} opacity="0.2" data-id="element-118" data-mp="1h258uf67api6" />
          <circle r="5.4" fill={`url(#${uid}-dot)`} data-id="element-119" data-mp="tazk1aia8mvq" />
          <circle cx="-1.6" cy="-1.6" r="1.8" fill="#FFFFFF" opacity="0.8" data-id="element-120" data-mp="tdvjj39s3jma" />
          {animate && <animateMotion dur="8s" repeatCount="indefinite" calcMode="linear" data-id="element-121" data-mp="oeiuqczck2h2">
              <mpath href={`#${uid}-track-path`} data-id="element-122" data-mp="12d8wi5x42qbr" />
            </animateMotion>}
        </g>
      </g>
    </svg>;
}
function Motifs({
  motif,
  size,
  uid,
  animate
}: MotifProps & {
  motif: Motif;
}) {
  if (motif === 'atom') return <AtomMotif size={size} uid={uid} animate={animate} data-id="element-123" data-mp="2dtpbxz8o16ym" />;
  if (motif === 'molecule') return <MoleculeMotif size={size} uid={uid} animate={animate} data-id="element-124" data-mp="29prf5v6tc88q" />;
  return <ElectronOrbitMotif size={size} uid={uid} animate={animate} data-id="element-125" data-mp="2h8iglujao4a1" />;
}

/**
 * Chemistry Orbit Flow — globalna warstwa dekoracyjna.
 * Ilustracje wpływają zza prawej krawędzi, opadają prawym pasem,
 * a na końcu zakręcają łagodnie w lewo i zanikają.
 */
export function ChemistryOrbitFlow() {
  const isMobile = useIsMobile();
  const [visible, setVisible] = useState(false);
  const [staticOnly, setStaticOnly] = useState(false);
  useEffect(() => {
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const narrowQuery = window.matchMedia('(max-width: 420px)');
    const update = () => {
      setStaticOnly(motionQuery.matches);
      setVisible(!narrowQuery.matches);
    };
    update();
    motionQuery.addEventListener('change', update);
    narrowQuery.addEventListener('change', update);
    return () => {
      motionQuery.removeEventListener('change', update);
      narrowQuery.removeEventListener('change', update);
    };
  }, []);
  const count = isMobile ? 1 : 2;
  const items = useMemo(() => buildItems(count, isMobile), [count, isMobile]);
  const [motifShift, setMotifShift] = useState<number[]>(() => items.map(() => 0));
  useEffect(() => {
    setMotifShift(items.map(() => 0));
  }, [items]);
  if (!visible) return null;
  return <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true" role="presentation" data-id="element-126" data-mp="19whpk0ypyq6q">
      {items.map(item => {
      const motif = MOTIFS[(MOTIFS.indexOf(item.motif) + (motifShift[item.id] ?? 0)) % MOTIFS.length];
      return <span key={item.id} onAnimationIteration={() => setMotifShift(prev => prev.map((v, idx) => idx === item.id ? v + 1 : v))} className={staticOnly ? 'absolute block' : 'chem-orbit-flow absolute top-0 block'} style={staticOnly ? {
        right: `${item.right}vw`,
        top: `${20 + item.id * 36}vh`,
        opacity: item.opacity * 0.8
      } : {
        right: `${item.right}vw`,
        animationDuration: `${item.duration}s`,
        animationDelay: `${item.delay}s`,
        '--orbit-curve': `${item.curve}px`,
        '--orbit-opacity': item.opacity
      } as React.CSSProperties} data-id="element-127" data-mp="22iizdltgifws">
          <Motifs motif={motif} size={item.size} uid={`orbit-${item.id}-${motif}`} animate={!staticOnly} data-id="element-128" data-mp="c0cfflouyajc" />
        </span>;
    })}
    </div>;
}